/* Web Push: регистрация Service Worker, запрос разрешения и подписка устройства. */

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5252/api');

function authHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : null;
}

export function pushSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await registration.update().catch(() => {});
    return registration;
  } catch (e) {
    console.error('Не удалось зарегистрировать Service Worker:', e);
    return null;
  }
}

async function getVapidKey() {
  try {
    const res = await fetch(`${API_URL}/push/vapid-public-key`);
    if (!res.ok) return null;
    const { publicKey } = await res.json();
    return publicKey || null;
  } catch (e) {
    return null;
  }
}

/**
 * Запрашивает разрешение и подписывает устройство на Web Push.
 * @returns 'granted' | 'denied' | 'default' | 'unsupported' | 'error'
 */
export async function enablePush() {
  if (!pushSupported()) return 'unsupported';

  const headers = authHeaders();
  if (!headers) return 'unauthorized';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission; // 'denied' | 'default'

  const reg = (await navigator.serviceWorker.getRegistration()) || (await registerServiceWorker());
  if (!reg) return 'error';
  await navigator.serviceWorker.ready;

  const vapid = await getVapidKey();
  if (!vapid) {
    console.error('VAPID-ключ недоступен (бэкенд: flask --app app gen-vapid)');
    return 'error';
  }

  try {
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid),
      });
    }

    const res = await fetch(`${API_URL}/push/subscribe`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ subscription: sub.toJSON() }),
    });
    if (!res.ok) return 'error';
    return 'granted';
  } catch (e) {
    console.error('Ошибка подписки на Web Push:', e);
    return 'error';
  }
}

/** Реальное состояние устройства: разрешение браузера + подписка + запись API. */
export async function getPushState() {
  if (!pushSupported()) return { supported: false, permission: 'unsupported', subscribed: false };
  const permission = Notification.permission;
  if (permission !== 'granted') return { supported: true, permission, subscribed: false };

  const registration = (await navigator.serviceWorker.getRegistration('/')) || await registerServiceWorker();
  const subscription = registration ? await registration.pushManager.getSubscription() : null;
  if (!subscription) return { supported: true, permission, subscribed: false };

  const headers = authHeaders();
  if (!headers) return { supported: true, permission, subscribed: false };
  try {
    const res = await fetch(`${API_URL}/push/status`, {
      method: 'POST', headers, body: JSON.stringify({ endpoint: subscription.endpoint }),
    });
    const data = res.ok ? await res.json() : {};
    return { supported: true, permission, subscribed: !!data.subscribed };
  } catch (_) {
    return { supported: true, permission, subscribed: false };
  }
}

export async function disablePush() {
  if (!pushSupported()) return 'unsupported';
  const registration = await navigator.serviceWorker.getRegistration('/');
  const subscription = registration ? await registration.pushManager.getSubscription() : null;
  if (!subscription) return 'disabled';

  const headers = authHeaders();
  if (headers) {
    await fetch(`${API_URL}/push/unsubscribe`, {
      method: 'POST', headers, body: JSON.stringify({ endpoint: subscription.endpoint }),
    }).catch(() => {});
  }
  const removed = await subscription.unsubscribe();
  return removed ? 'disabled' : 'error';
}

/** Отвязать браузер от аккаунта при logout, сохранив системную подписку.
 * После следующего входа пользователь сможет привязать её одним нажатием. */
export async function detachPushFromAccount() {
  if (!pushSupported()) return;
  const headers = authHeaders();
  if (!headers) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    const subscription = registration ? await registration.pushManager.getSubscription() : null;
    if (!subscription) return;
    await fetch(`${API_URL}/push/unsubscribe`, {
      method: 'POST', headers, body: JSON.stringify({ endpoint: subscription.endpoint }), keepalive: true,
    });
  } catch (_) { /* logout не должен блокироваться из-за недоступного API */ }
}
